import {
  Card,
  CardBody,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react'
import type { ReactNode } from 'react'

export type ReportColumn<T extends Record<string, unknown>> = {
  key: string
  header: string
  render?: (row: T) => ReactNode
  isTruncated?: boolean
  maxW?: string
  whiteSpace?: string
}

export type ReportDataTableProps<T extends Record<string, unknown>> = {
  title?: string
  subtitle?: string
  columns: ReportColumn<T>[]
  rows: T[]
  loading?: boolean
  loadingHeight?: string | number
  emptyMessage?: string
  getRowKey: (row: T, index: number) => string | number
  footer?: ReactNode
}

export default function ReportDataTable<T extends Record<string, unknown>>({
  title,
  subtitle,
  columns,
  rows,
  loading = false,
  loadingHeight = '240px',
  emptyMessage = 'No data for the current filters.',
  getRowKey,
  footer,
}: ReportDataTableProps<T>) {
  return (
    <Card>
      <CardBody overflowX="auto">
        {(title || subtitle) && (
          <Flex justify="space-between" align="center" mb={3} flexWrap="wrap" gap={2}>
            {title ? (
              <Text fontWeight="semibold" fontSize="sm">
                {title}
              </Text>
            ) : (
              <span />
            )}
            {subtitle ? (
              <Text fontSize="xs" color="gray.500">
                {subtitle}
              </Text>
            ) : null}
          </Flex>
        )}

        {loading ? (
          <Skeleton height={loadingHeight} />
        ) : rows.length === 0 ? (
          <Text fontSize="sm" color="gray.500" py={8} textAlign="center">
            {emptyMessage}
          </Text>
        ) : (
          <>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  {columns.map((col) => (
                    <Th key={col.key} whiteSpace={col.whiteSpace ?? 'nowrap'}>
                      {col.header}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {rows.map((row, index) => (
                  <Tr key={getRowKey(row, index)}>
                    {columns.map((col) => (
                      <Td
                        key={col.key}
                        maxW={col.maxW}
                        isTruncated={col.isTruncated}
                        title={col.isTruncated ? String(row[col.key] ?? '') : undefined}
                      >
                        {col.render ? col.render(row) : String(row[col.key] ?? '—')}
                      </Td>
                    ))}
                  </Tr>
                ))}
              </Tbody>
            </Table>
            {footer}
          </>
        )}
      </CardBody>
    </Card>
  )
}
